import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Loader2, Tag, MapPin, Clock, Eye, ChevronRight, Home as HomeIcon,
  Phone, Mail, LinkIcon, Share2, Facebook, Heart, Lock,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import userApi from "@/lib/userApi";
import { useAppAuth } from "@/context/AppAuthContext";
import Layout from "@/components/Layout";
import ArticleGallery from "@/components/ArticleGallery";
import Seo from "@/components/Seo";
import { timeAgo, formatDate, formatViews } from "@/lib/format";

export default function ListingDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAppAuth();
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const resolve = (src) => (src && src.startsWith("/") ? `${backend}${src}` : src);

  const { data: listing, isLoading, isError } = useQuery({
    queryKey: ["listing", slug, !!user],
    queryFn: () => userApi.get(`/listings/${slug}`).then((r) => r.data),
    retry: false,
  });

  // Contact details are only returned to logged-in users (never sent to anonymous).
  const { data: contacts } = useQuery({
    queryKey: ["listing-contacts", slug],
    queryFn: () => userApi.get(`/listings/${slug}/contacts`).then((r) => r.data),
    enabled: !!user && !!listing,
    retry: false,
  });

  const [fav, setFav] = useState(false);
  const [favBusy, setFavBusy] = useState(false);
  useEffect(() => { setFav(!!listing?.is_favorite); }, [listing]);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  const toggleFav = async () => {
    if (!user) {
      navigate("/vhod", { state: { from: `/obyava/${slug}` } });
      return;
    }
    setFavBusy(true);
    try {
      if (fav) {
        await userApi.delete(`/account/favorites/${listing.id}`);
        setFav(false);
        toast.success("Премахнато от любими");
      } else {
        await userApi.post(`/account/favorites/${listing.id}`);
        setFav(true);
        toast.success("Запазено в любими");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setFavBusy(false);
    }
  };

  const shareUrl = listing?.slug ? `${backend}/api/share/listing/${listing.slug}` : "";
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: listing?.title, url: shareUrl }); } catch (e) { /* cancelled */ }
    } else {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Връзката е копирана");
    }
  };
  const shareFacebook = () =>
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, "_blank", "noopener,noreferrer,width=640,height=640");
  const shareX = () =>
    window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(listing?.title || "")}`, "_blank", "noopener,noreferrer,width=640,height=640");

  if (isLoading) {
    return (
      <Layout>
        <div className="flex min-h-[60vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (isError || !listing) {
    return (
      <Layout>
        <Seo title="Обявата не е намерена" noindex />
        <div className="mx-auto max-w-2xl px-4 py-24 text-center">
          <h1 className="font-display text-3xl font-extrabold text-slate-900">Обявата не е намерена</h1>
          <p className="mt-3 text-slate-600">Възможно е връзката да е грешна или обявата да е премахната.</p>
          <Link to="/obyavi" className="mt-6 inline-flex rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#0041A8]">
            Всички обяви
          </Link>
        </div>
      </Layout>
    );
  }

  const gallery = (listing.images || []).map((url) => ({ url: resolve(url), alt: "" }));

  return (
    <Layout>
      <Seo
        title={listing.title}
        description={listing.description?.slice(0, 200)}
        image={resolve((listing.images || [])[0])}
      />
      <article className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8" data-testid="listing-detail">
        <nav className="flex items-center gap-1.5 text-xs text-slate-500">
          <Link to="/" className="inline-flex items-center gap-1 hover:text-primary">
            <HomeIcon className="h-3.5 w-3.5" />Начало
          </Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <Link to="/obyavi" className="hover:text-primary">Обяви</Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="text-slate-700">{listing.listing_type}</span>
        </nav>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1 rounded-full bg-primary px-2.5 py-0.5 text-xs font-bold text-white">
            <Tag className="h-3 w-3" />{listing.listing_type}
          </span>
        </div>

        <h1 className="mt-3 font-display text-3xl font-extrabold leading-tight text-slate-900 sm:text-4xl" data-testid="listing-detail-title">
          {listing.title}
        </h1>

        {listing.price && (
          <div className="mt-3 font-display text-2xl font-extrabold text-primary" data-testid="listing-detail-price">
            {listing.price}
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-y border-slate-200 py-3">
          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
            <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" />{listing.location}</span>
            <span className="inline-flex items-center gap-1"><Clock className="h-4 w-4" />{timeAgo(listing.published_at)}</span>
            <span className="inline-flex items-center gap-1"><Eye className="h-4 w-4" />{formatViews(listing.views)}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={toggleFav}
              disabled={favBusy}
              data-testid="listing-favorite-btn"
              aria-label={fav ? "Премахни от любими" : "Запази в любими"}
              className={`inline-flex h-9 w-9 items-center justify-center rounded-full border transition-colors ${fav ? "border-red-200 bg-red-50 text-red-500" : "border-slate-200 text-slate-600 hover:border-red-300 hover:text-red-500"}`}
            >
              <Heart className={`h-4 w-4 ${fav ? "fill-red-500" : ""}`} />
            </button>
            <button onClick={shareFacebook} data-testid="listing-share-facebook" aria-label="Сподели във Facebook" className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-600 transition-colors hover:border-[#1877F2] hover:text-[#1877F2]">
              <Facebook className="h-4 w-4" />
            </button>
            <button onClick={shareX} data-testid="listing-share-x" aria-label="Сподели в X" className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-600 transition-colors hover:border-slate-900 hover:text-slate-900">
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="h-[15px] w-[15px]">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </button>
            <button onClick={share} data-testid="listing-share-btn" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:border-primary hover:text-primary">
              <Share2 className="h-4 w-4" />Сподели
            </button>
          </div>
        </div>

        {gallery.length > 0 && <ArticleGallery items={gallery} />}

        <div className="mt-6 whitespace-pre-line text-[17px] leading-relaxed text-slate-800" data-testid="listing-detail-description">
          {listing.description}
        </div>

        <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50 p-5" data-testid="listing-contact">
          <h2 className="font-display text-lg font-bold text-slate-900">Свържете се с подателя</h2>
          {!listing.has_contacts ? (
            <p className="mt-3 text-sm text-slate-500" data-testid="listing-contacts-none">
              Подателят не е посочил контакти за тази обява.
            </p>
          ) : authLoading ? (
            <div className="mt-3 flex items-center gap-2 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin" />Зареждане…</div>
          ) : user ? (
            <div className="mt-3 space-y-2 text-sm" data-testid="listing-contacts-revealed">
              <div className="font-semibold text-slate-800">{(contacts || listing).contact_name}</div>
              {!contacts ? (
                <div className="flex items-center gap-2 text-slate-400"><Loader2 className="h-4 w-4 animate-spin" />Зареждане…</div>
              ) : (
                <>
                  {contacts.contact_phone && (
                    <a href={`tel:${contacts.contact_phone}`} data-testid="listing-contact-phone" className="flex items-center gap-2 text-slate-700 hover:text-primary">
                      <Phone className="h-4 w-4" />{contacts.contact_phone}
                    </a>
                  )}
                  {contacts.contact_email && (
                    <a href={`mailto:${contacts.contact_email}`} data-testid="listing-contact-email" className="flex items-center gap-2 text-slate-700 hover:text-primary">
                      <Mail className="h-4 w-4" />{contacts.contact_email}
                    </a>
                  )}
                  {contacts.contact_social && (
                    <a href={contacts.contact_social} target="_blank" rel="noreferrer noopener" data-testid="listing-contact-social" className="flex items-center gap-2 break-all text-slate-700 hover:text-primary">
                      <LinkIcon className="h-4 w-4 shrink-0" />{contacts.contact_social}
                    </a>
                  )}
                  {!contacts.contact_phone && !contacts.contact_email && !contacts.contact_social && (
                    <p className="text-slate-400">Подателят не е посочил контакти.</p>
                  )}
                </>
              )}
            </div>
          ) : (
            <div className="mt-3 flex flex-col items-start gap-3" data-testid="listing-contacts-locked">
              <p className="flex items-center gap-2 text-sm text-slate-500">
                <Lock className="h-4 w-4" />
                Контактите са видими само за регистрирани потребители.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => navigate("/vhod", { state: { from: `/obyava/${slug}` } })}
                  data-testid="listing-contacts-login-btn"
                  className="rounded-full bg-primary px-4 py-2 text-sm font-bold text-white hover:bg-[#0041A8]"
                >
                  Вход
                </button>
                <button
                  onClick={() => navigate("/registraciya", { state: { from: `/obyava/${slug}` } })}
                  data-testid="listing-contacts-register-btn"
                  className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:border-primary hover:text-primary"
                >
                  Регистрация
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 text-xs text-slate-400">
          Публикувано на {formatDate(listing.published_at)}
          {listing.expires_at && <> · валидна до {formatDate(listing.expires_at)}</>}
        </div>

        <div className="mt-8">
          <Link to="/obyavi" className="text-sm font-semibold text-primary hover:underline">← Всички обяви</Link>
        </div>
      </article>
    </Layout>
  );
}
